/**
 * Resolve whether a route subtree should bypass `<RootRuntime>` and render
 * its `default` Component directly during SSR.
 *
 * Decision matrix:
 * - No `ssrShell` flag → CSR (`false`).
 * - `ssrShell: true`, no experiment → forced SSR (`true`).
 * - `ssrShell: true` + experiment → SSR only when the flag is `'on'`.
 *
 * SSR-safe: reads from server-injected variants via `useHydratedFeatureFlag`.
 * Client-only `localStorage` override (`ssr::shell-override:<flagName>` →
 * `'on' | 'off'`) for QA/dev use without touching Amplitude.
 */
import type { UIMatch } from 'react-router'
import type { ValueOf } from 'type-fest'
import { FeatureFlags } from 'src/experiments/featureFlags'
import { useHydratedFeatureFlag } from 'src/experiments/featureFlags/hooks'
import { getRouteHandleFlag } from './route'

const SSR_SHELL_OVERRIDE_PREFIX = 'ssr::shell-override:'

/** The single Amplitude flag controlling every dual-mode route's SSR/CSR branch. */
export const SSR_DUAL_MODE_EXPERIMENT_NAME = FeatureFlags.SsrDualMode

/**
 * Pre-shaped `ssrShellExperiment` object for route `handle` exports:
 * ```tsx
 * export const handle = { ssrShell: true, ssrShellExperiment: SSR_DUAL_MODE_EXPERIMENT }
 * ```
 */
export const SSR_DUAL_MODE_EXPERIMENT = {
  name: SSR_DUAL_MODE_EXPERIMENT_NAME,
} as const

function readShellOverride(flagName: string): 'on' | 'off' | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    const raw = window.localStorage.getItem(
      `${SSR_SHELL_OVERRIDE_PREFIX}${flagName}`,
    )
    if (raw === 'on' || raw === 'off') return raw
    return undefined
  } catch {
    return undefined
  }
}

export interface SsrShellDecisionInput {
  ssrShell: boolean
  experiment: { name: string } | undefined
  /** `true` when the global Stage 2 flag is `'on'` (Full SSR). */
  flagOn: boolean
  /** Client-only localStorage override; takes precedence over `flagOn`. */
  override: 'on' | 'off' | undefined
}

/** Pure function for the SSR-shell decision — testable without a React Router context. */
export function resolveSsrShellDecision({
  ssrShell,
  experiment,
  flagOn,
  override,
}: SsrShellDecisionInput): boolean {
  if (!ssrShell) return false
  if (!experiment) return true
  if (override === 'on') return true
  if (override === 'off') return false
  return flagOn
}

/** Compute the SSR-shell decision for the active route subtree. Call inside the React Router runtime. */
export function useResolvedSsrShell(matches: UIMatch[]): boolean {
  const ssrShell = Boolean(getRouteHandleFlag(matches, 'ssrShell'))
  const experiment = getRouteHandleFlag(matches, 'ssrShellExperiment')

  // Must be called unconditionally to satisfy Rules of Hooks.
  const flagOn = useHydratedFeatureFlag(
    (experiment?.name as ValueOf<typeof FeatureFlags>) ??
      SSR_DUAL_MODE_EXPERIMENT_NAME,
  )

  return resolveSsrShellDecision({
    ssrShell,
    experiment,
    flagOn,
    override: experiment ? readShellOverride(experiment.name) : undefined,
  })
}

/** Returns the `localStorage` key for a given flag name (for DebugPanel / tooling). */
export function getSsrShellOverrideKey(flagName: string): string {
  return `${SSR_SHELL_OVERRIDE_PREFIX}${flagName}`
}
